import { Grid } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ToolboxForm } from 'shared/components/common/toolbox-form';
import { ProjectForm } from 'shared/components/project-forms/components/project-form';
import { Header, Page } from 'redesign';
import { useAlert } from 'shared/components/alerts';
import { HQProjectFormTypeCreate } from 'shared/components/project-forms/types/project-form.types';
import { invariant } from 'shared/utils/utils';
import { useCallback, useEffect } from 'react';
import { WorkTemplateDetail } from 'api';
import { mapWorkTemplateToDetails } from 'api/services/work-template/work-template.utils';
import { stringifyFilters } from 'shared/utils/stringify-filters';
import { useExternalParams } from 'shared/hooks';
import { DateTime } from 'luxon';
import { useCompanyRelativePath } from '../../../../../../../hooks';
import { routes } from '../../../../../../../Routes';
import { useCreateProject } from '../../../hooks/use-create-project';
import { OgpProjectCreatePayload } from '../../../../../../../services/project-service.types';
import { PROJECT_PLANNING_INITIAL_VALUES } from '../../../filters/hooks/use-project-planning-filters';

type ProjectCreateProps = {
  successMessage: string;
  pageTitle: string;
  workTemplateDetail: WorkTemplateDetail | undefined;
};

export const ProjectCreateFormPage = ({
  successMessage,
  pageTitle,
  workTemplateDetail,
}: ProjectCreateProps) => {
  const { alertSuccess, alertError } = useAlert();
  const formMethods = useForm<HQProjectFormTypeCreate>({ defaultValues: { location: null } });

  const company = formMethods.watch('companyOption');
  const companyId = company?.value;

  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();
  const mutation = useCreateProject();
  const [params] = useExternalParams(PROJECT_PLANNING_INITIAL_VALUES);
  const formattedParams = stringifyFilters({
    ...params,
    toDate: params.toDate
      ? params.toDate.toFormat('yyyy-MM-dd')
      : DateTime.now().toFormat('yyyy-MM-dd'),
  });
  useEffect(() => {
    if (companyId) {
      formMethods.reset({ companyOption: company }, { keepDefaultValues: true });
    }
  }, [companyId]);

  const handleSubmit = useCallback(() => {
    formMethods.handleSubmit((data: HQProjectFormTypeCreate) => {
      invariant(data.location, 'Location is required');

      const payload: OgpProjectCreatePayload = {
        departmentId: data.departmentId,
        jobId: data.jobId,
        startDate: data.startDate,
        endDate: data.endDate,
        weekHourVolume: +data.weeklyHours,
        freelanceProperties: data.isFreelance
          ? {
              hourlyRateCents: Math.round(+data.hourlyRate * 100),
              ortApplicable: data.isFreelanceOrtApplicable,
            }
          : undefined,
        tempWorkProperties: data.isTempwork
          ? {
              ortApplicable: data.isTempworkOrtApplicable,
            }
          : undefined,
        location: data.location,
      };
      mutation.mutate(payload, {
        onSuccess: ({ id }) => {
          alertSuccess(successMessage);
          navigate(generatePath(routes.Projects, {}, { id, ...formattedParams }));
        },
        onError: (e) => alertError(e),
      });
    })();
  }, []);

  return (
    <FormProvider {...formMethods}>
      <Page
        header={<Header titleText={pageTitle} />}
        toolbox={
          <ToolboxForm
            onSubmit={handleSubmit}
            mutationStatus={mutation.status}
            mode="create"
            onCancel={() =>
              navigate(generatePath(routes.Projects, {}, formattedParams), { replace: true })
            }
          />
        }
      >
        <Grid container>
          <Grid item xs={12}>
            <ProjectForm
              mode="create"
              isProjectWithActivePlacement={false}
              workDetails={mapWorkTemplateToDetails(workTemplateDetail)}
            />
          </Grid>
        </Grid>
      </Page>
    </FormProvider>
  );
};
